/**
 * SUPRIMENTOS > COMPRAS > NECESSIDADES.
 *
 * "O que está abaixo do ponto, por fornecedor, com quantidade e preço sugeridos"
 * — é o valor da Onda 3 segundo o PLANO, e é a tela que responde a pergunta que
 * o dono faz de manhã antes de qualquer ordem existir.
 *
 * NECESSIDADE NÃO É PEDIDO. Ela é o que a fase 1 da §10.5 detecta por SKU; o
 * pedido é montado por fornecedor, na fase 2, dentro de uma janela de
 * consolidação. Essa distinção é a correção mais importante do algoritmo — nove
 * insumos do mesmo hortifrúti são nove necessidades e UM pedido — e a tela
 * agrupa por fornecedor justamente para mostrar isso antes de a ordem nascer.
 *
 * OS ESTADOS, e por que são cinco: `viva` está esperando consolidação;
 * `consolidada` já virou linha de ordem; `devolvida` saiu da ordem porque a
 * reavaliação achou saldo acima do ponto; `recusada` o dono disse não;
 * `expirada` o fornecedor não respondeu. Devolvida e recusada não renascem
 * antes do re-arme, e a coluna "motivo" diz isso em texto.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as AlfredoComponents from "../../../components";
import * as AlfredoStyles from "../../../styles/global/global";
import * as Estilo from "../../../styles/components/compras/ComprasComum";
import comprasService, {
  AlertaDeCompra,
  Necessidade,
  comoDataHora,
  comoQuantidade,
  comoReais,
} from "../../../services/compras.service";

const ESTADOS = ["viva", "consolidada", "devolvida", "recusada", "expirada"];

function tomDoEstado(estado: string): "ok" | "atencao" | "info" | "erro" {
  if (estado === "viva") return "atencao";
  if (estado === "consolidada") return "ok";
  if (estado === "recusada" || estado === "expirada") return "erro";
  return "info";
}

const NecessitiesPage: React.FC = () => {
  const [parametrosDaUrl, definirParametrosDaUrl] = useSearchParams();
  const estado = parametrosDaUrl.get("estado") ?? "viva";
  const busca = parametrosDaUrl.get("busca") ?? "";

  const [necessidades, definirNecessidades] = useState<Necessidade[]>([]);
  const [alertas, definirAlertas] = useState<AlertaDeCompra[]>([]);
  const [omitido, definirOmitido] = useState<string[]>([]);
  const [carregando, definirCarregando] = useState(true);
  const [erro, definirErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    definirCarregando(true);
    definirErro(null);
    try {
      const [lista, avisos] = await Promise.all([
        comprasService.necessidades({
          estado: estado || undefined,
          busca: busca || undefined,
          limit: 200,
        }),
        comprasService.alertas({ estado: "aberto", limit: 50 }),
      ]);
      definirNecessidades(lista.dados);
      definirOmitido(lista.omitido ?? []);
      definirAlertas(avisos.dados);
    } catch (e: any) {
      definirErro(
        e?.response?.data?.message ?? "Não foi possível ler as necessidades.",
      );
    } finally {
      definirCarregando(false);
    }
  }, [estado, busca]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /**
   * Agrupado POR FORNECEDOR, porque é assim que o pedido vai ser montado. Ver
   * nove linhas soltas esconde justamente o que a consolidação resolve.
   */
  const porFornecedor = useMemo(() => {
    // `Record` e não `Map`: o `target` deste projeto não permite iterar um
    // `Map` sem `downlevelIteration`, e mexer no tsconfig por causa de uma tela
    // mudaria a compilação do sistema inteiro.
    const grupos: Record<string, { nome: string; linhas: Necessidade[] }> = {};
    const ordem: string[] = [];
    for (const linha of necessidades) {
      const chave = linha.fornecedor ?? "(sem fornecedor)";
      if (!grupos[chave]) {
        grupos[chave] = {
          nome:
            linha.fornecedorNome ??
            "Sem fornecedor definido no limite de reposição",
          linhas: [],
        };
        ordem.push(chave);
      }
      grupos[chave].linhas.push(linha);
    }
    return ordem.map((chave) => [chave, grupos[chave]] as const);
  }, [necessidades]);

  const trocar = (chave: string, valor: string) => {
    const novos = new URLSearchParams(parametrosDaUrl);
    if (valor) novos.set(chave, valor);
    else novos.delete(chave);
    definirParametrosDaUrl(novos);
  };

  return (
    <AlfredoStyles.Container>
      <AlfredoComponents.Breadcrumb />
      <AlfredoStyles.FlexContainer>
        <AlfredoStyles.FlexItem>
          <Estilo.Cabecalho>
            <div>
              <Estilo.Titulo>O que está abaixo do ponto</Estilo.Titulo>
              <Estilo.Subtitulo>
                Necessidades detectadas por SKU, agrupadas pelo fornecedor que vai
                atendê-las. Uma necessidade não é um pedido: o pedido é montado por
                fornecedor, quando a janela de consolidação fecha.
              </Estilo.Subtitulo>
            </div>
          </Estilo.Cabecalho>

          {alertas.length > 0 && (
            <Estilo.Painel tom="atencao">
              <Estilo.TituloDoPainel>Alertas abertos</Estilo.TituloDoPainel>
              <Estilo.ListaDeMotivos>
                {alertas.map((alerta) => (
                  <li key={alerta.alertaUuid}>
                    <Estilo.Forte>{alerta.titulo}</Estilo.Forte>
                    {alerta.detalhe ? ` — ${alerta.detalhe}` : ""}
                  </li>
                ))}
              </Estilo.ListaDeMotivos>
            </Estilo.Painel>
          )}

          <Estilo.Filtros>
            <Estilo.Campo>
              Estado
              <Estilo.Selecao
                value={estado}
                onChange={(e) => trocar("estado", e.target.value)}
              >
                <option value="">todos</option>
                {ESTADOS.map((valor) => (
                  <option key={valor} value={valor}>
                    {valor}
                  </option>
                ))}
              </Estilo.Selecao>
            </Estilo.Campo>
            <Estilo.Campo>
              Insumo
              <Estilo.Entrada
                defaultValue={busca}
                placeholder="nome ou código"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    trocar("busca", (e.target as HTMLInputElement).value);
                  }
                }}
              />
            </Estilo.Campo>
            <Estilo.BotaoSecundario type="button" onClick={carregar}>
              Atualizar
            </Estilo.BotaoSecundario>
          </Estilo.Filtros>

          {erro && (
            <Estilo.Painel tom="erro">
              <Estilo.Texto>{erro}</Estilo.Texto>
            </Estilo.Painel>
          )}

          {carregando && <Estilo.Vazio>Lendo as necessidades…</Estilo.Vazio>}

          {!carregando && necessidades.length === 0 && (
            <Estilo.Vazio>
              Nenhuma necessidade neste filtro.
              <br />
              Isso pode significar que nada cruzou o ponto de reposição — ou que o
              motor ainda não conseguiu avaliar o saldo. Sem histórico de movimento
              de estoque, o gatilho <strong>não</strong> supõe saldo zero: ele
              bloqueia e registra o motivo em Alertas, porque zero diria que o
              estoque foi contado e deu zero.
            </Estilo.Vazio>
          )}

          {porFornecedor.map(([chave, grupo]) => (
            <div key={chave} style={{ marginBottom: "var(--sp-20)" }}>
              <Estilo.Cabecalho>
                <div>
                  <Estilo.TituloDoPainel>{grupo.nome}</Estilo.TituloDoPainel>
                  <Estilo.Subtitulo>
                    {grupo.linhas.length} insumo(s) — viram{" "}
                    <Estilo.Forte>uma</Estilo.Forte> ordem quando a janela fechar
                  </Estilo.Subtitulo>
                </div>
              </Estilo.Cabecalho>

              <Estilo.Tabela>
                <thead>
                  <tr>
                    <Estilo.Cabecalhos>Insumo</Estilo.Cabecalhos>
                    <Estilo.Cabecalhos>Loja</Estilo.Cabecalhos>
                    <Estilo.Cabecalhos $numerica>Saldo</Estilo.Cabecalhos>
                    <Estilo.Cabecalhos $numerica>Ponto</Estilo.Cabecalhos>
                    <Estilo.Cabecalhos $numerica>Sugerido</Estilo.Cabecalhos>
                    <Estilo.Cabecalhos $numerica>Preço</Estilo.Cabecalhos>
                    <Estilo.Cabecalhos $numerica>Cobertura</Estilo.Cabecalhos>
                    <Estilo.Cabecalhos>Situação</Estilo.Cabecalhos>
                  </tr>
                </thead>
                <tbody>
                  {grupo.linhas.map((linha: Necessidade) => {
                    // Cobertura = saldo ÷ consumo médio. Com consumo 0 a tela
                    // escreve "—", nunca infinito e nunca zero: zero diria "acaba
                    // hoje" sobre um insumo que ninguém consome.
                    const cobertura =
                      linha.consumoMedioDiario && linha.consumoMedioDiario > 0
                        ? (linha.saldoNoDisparo / linha.consumoMedioDiario).toFixed(1)
                        : "—";
                    return (
                      <tr key={linha.necessidadeUuid}>
                        <Estilo.Celula>
                          {linha.produto ?? linha.skuUuid}
                          {linha.urgente && (
                            <>
                              {" "}
                              <Estilo.Selo tom="erro">urgente</Estilo.Selo>
                            </>
                          )}
                          {linha.origemDaRegra === "completacao" && (
                            <>
                              {" "}
                              <Estilo.Selo tom="info">completação</Estilo.Selo>
                            </>
                          )}
                        </Estilo.Celula>
                        <Estilo.Celula>
                          {linha.lojaNome ?? linha.subsidiary}
                        </Estilo.Celula>
                        <Estilo.Celula $numerica>
                          {comoQuantidade(linha.saldoNoDisparo)}
                        </Estilo.Celula>
                        <Estilo.Celula $numerica>
                          {comoQuantidade(linha.pontoNoDisparo)}
                        </Estilo.Celula>
                        <Estilo.Celula $numerica>
                          {comoQuantidade(linha.quantidade)}{" "}
                          {linha.unidadeCompra ?? ""}
                        </Estilo.Celula>
                        <Estilo.Celula $numerica $alerta={linha.semPreco}>
                          {linha.semPreco
                            ? "sem preço vigente"
                            : comoReais(linha.precoUnitario)}
                        </Estilo.Celula>
                        <Estilo.Celula $numerica>{cobertura}</Estilo.Celula>
                        <Estilo.Celula>
                          <Estilo.Selo tom={tomDoEstado(linha.estado)}>
                            {linha.estado}
                          </Estilo.Selo>
                          <div style={{ marginTop: "var(--sp-4)", opacity: 0.75 }}>
                            {linha.motivoDoEstado}
                          </div>
                          <div style={{ marginTop: "var(--sp-4)", opacity: 0.6 }}>
                            detectada em {comoDataHora(linha.detectadaEm)}
                          </div>
                        </Estilo.Celula>
                      </tr>
                    );
                  })}
                </tbody>
              </Estilo.Tabela>
            </div>
          ))}

          {omitido.length > 0 && (
            <Estilo.Vazio>
              Alguns números não vieram do servidor porque sua permissão não os
              alcança: {omitido.join(", ")}. Eles aparecem como “—”, e não como zero.
            </Estilo.Vazio>
          )}
        </AlfredoStyles.FlexItem>
      </AlfredoStyles.FlexContainer>
    </AlfredoStyles.Container>
  );
};

export default NecessitiesPage;
